import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { PortalDsBadge } from "src/components/portal-design-system/portal-ds-badge";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { formatCurrency } from "../../utils/billing-utils";
import type { PayoutUser } from "../types/commissions.types";
import { CommissionTableEmptyState } from "./commission-table-empty-state";

interface ActivationTeamTableProps {
  users: PayoutUser[];
  quarterLabel: string;
  onRowClick: (user: PayoutUser) => void;
  /** Optional map of user id -> summed estimated commission for the period (from reports) */
  userIdToEstimatedCommission?: Record<string, number>;
}

function hasQuarterOverride(user: PayoutUser): boolean {
  return user.percentByQuarter != null && Object.keys(user.percentByQuarter).length > 0;
}

const ActivationTeamTable: React.FC<ActivationTeamTableProps> = ({
  users,
  quarterLabel,
  onRowClick,
  userIdToEstimatedCommission,
}) => {
  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={portalDs.tableHeadCell}>Name</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Email</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Rate</TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Est. Commission ({quarterLabel})
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <CommissionTableEmptyState
              colSpan={4}
              title="No activation team users yet"
              description="Add a user to set commission rates and track estimated payouts."
            />
          ) : (
            users.map((user) => (
              <TableRow
                key={user.id}
                className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive)}
                onClick={() => onRowClick(user)}
              >
                <TableCell className={cn(portalDs.tableCell, "font-medium")}>
                  <span className="inline-flex items-center gap-2">
                    {user.name}
                    {hasQuarterOverride(user) && (
                      <span title="Has per-quarter rate override">
                        <PortalDsBadge variant="amber">Override</PortalDsBadge>
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {user.email}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {user.activePercent}%
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {userIdToEstimatedCommission?.[user.id] != null
                    ? formatCurrency(userIdToEstimatedCommission[user.id])
                    : user.estimatedPayoutForQuarter != null
                      ? formatCurrency(user.estimatedPayoutForQuarter)
                      : "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ActivationTeamTable;
